import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import IframeNodeView from './iframe-node-view';

const DEFAULT_IFRAME_SRC = 'https://example.com';
const DEFAULT_IFRAME_WIDTH = '640';
const DEFAULT_IFRAME_HEIGHT = '360';
const DEFAULT_IFRAME_TITLE = 'Embedded content';
const DEFAULT_IFRAME_SANDBOX = 'true';
const DEFAULT_IFRAME_WILEY_HOSTED = 'false';
const DEFAULT_IFRAME_FRAMEBORDER = '0';

const isHttpsUrl = (value?: string | null): value is string => {
    if (!value) {
        return false;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return false;
    }

    try {
        const parsed = new URL(trimmedValue);
        return parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

const toNonEmptyString = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }

    const trimmedValue = value.trim();
    return trimmedValue || undefined;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        iframe: {
            /**
             * Add an iframe
             */
            insertIframe: (options?: {
                src?: string;
                width?: string;
                height?: string;
                title?: string;
                sandbox?: string;
                wileyHosted?: string;
                allowFullscreen?: boolean;
            }) => ReturnType;
        };
    }
}

export const IframeHeadless = Node.create({
    name: 'iframe',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: DEFAULT_IFRAME_SRC,
                parseHTML: element => element.getAttribute('src') ?? DEFAULT_IFRAME_SRC,
                renderHTML: attributes => (isHttpsUrl(attributes.src) ? { src: attributes.src } : { src: DEFAULT_IFRAME_SRC }),
            },
            width: {
                default: DEFAULT_IFRAME_WIDTH,
                parseHTML: element => toNonEmptyString(element.getAttribute('width')) ?? DEFAULT_IFRAME_WIDTH,
                renderHTML: attributes => ({
                    width: toNonEmptyString(attributes.width) ?? DEFAULT_IFRAME_WIDTH,
                }),
            },
            height: {
                default: DEFAULT_IFRAME_HEIGHT,
                parseHTML: element => toNonEmptyString(element.getAttribute('height')) ?? DEFAULT_IFRAME_HEIGHT,
                renderHTML: attributes => ({
                    height: toNonEmptyString(attributes.height) ?? DEFAULT_IFRAME_HEIGHT,
                }),
            },
            title: {
                default: DEFAULT_IFRAME_TITLE,
                parseHTML: element => toNonEmptyString(element.getAttribute('title')) ?? DEFAULT_IFRAME_TITLE,
                renderHTML: attributes => ({
                    title: toNonEmptyString(attributes.title) ?? DEFAULT_IFRAME_TITLE,
                }),
            },
            sandbox: {
                default: DEFAULT_IFRAME_SANDBOX,
                parseHTML: element => toNonEmptyString(element.getAttribute('sandbox')) ?? DEFAULT_IFRAME_SANDBOX,
                renderHTML: attributes => ({
                    sandbox: toNonEmptyString(attributes.sandbox) ?? DEFAULT_IFRAME_SANDBOX,
                }),
            },
            wileyHosted: {
                default: DEFAULT_IFRAME_WILEY_HOSTED,
                parseHTML: element => toNonEmptyString(element.getAttribute('wiley-hosted')) ?? DEFAULT_IFRAME_WILEY_HOSTED,
                renderHTML: attributes => ({
                    'wiley-hosted': toNonEmptyString(attributes.wileyHosted) ?? DEFAULT_IFRAME_WILEY_HOSTED,
                }),
            },
            allowFullscreen: {
                default: undefined,
                parseHTML: element => element.getAttribute('allowfullscreen') ?? undefined,
                renderHTML: attributes => ({
                    allowfullscreen: attributes.allowFullscreen,
                }),
            },
            frameborder: {
                default: DEFAULT_IFRAME_FRAMEBORDER,
                parseHTML: element => toNonEmptyString(element.getAttribute('frameborder')) ?? DEFAULT_IFRAME_FRAMEBORDER,
                renderHTML: attributes => ({
                    frameborder: toNonEmptyString(attributes.frameborder) ?? DEFAULT_IFRAME_FRAMEBORDER,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'iframe',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) {
                        return false;
                    }

                    const src = toNonEmptyString(element.getAttribute('src'));
                    if (!isHttpsUrl(src)) {
                        return false;
                    }

                    return {
                        src,
                        width: toNonEmptyString(element.getAttribute('width')) ?? DEFAULT_IFRAME_WIDTH,
                        height: toNonEmptyString(element.getAttribute('height')) ?? DEFAULT_IFRAME_HEIGHT,
                        title: toNonEmptyString(element.getAttribute('title')) ?? DEFAULT_IFRAME_TITLE,
                        sandbox: toNonEmptyString(element.getAttribute('sandbox')) ?? DEFAULT_IFRAME_SANDBOX,
                        wileyHosted: toNonEmptyString(element.getAttribute('wiley-hosted')) ?? DEFAULT_IFRAME_WILEY_HOSTED,
                        frameborder: toNonEmptyString(element.getAttribute('frameborder')) ?? DEFAULT_IFRAME_FRAMEBORDER,
                        allowFullscreen: element.getAttribute('allowfullscreen') ?? undefined,
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['iframe', HTMLAttributes, 0];
    },

    addCommands() {
        return {
            insertIframe:
                (options?: {
                    src?: string;
                    width?: string;
                    height?: string;
                    title?: string;
                    sandbox?: string;
                    wileyHosted?: string;
                    allowFullscreen?: boolean;
                }) =>
                ({ tr, dispatch }) => {
                    const src = toNonEmptyString(options?.src) ?? DEFAULT_IFRAME_SRC;
                    if (!isHttpsUrl(src)) {
                        return false;
                    }

                    const { selection } = tr;
                    const node = this.type.create({
                        src,
                        width: toNonEmptyString(options?.width) ?? DEFAULT_IFRAME_WIDTH,
                        height: toNonEmptyString(options?.height) ?? DEFAULT_IFRAME_HEIGHT,
                        title: toNonEmptyString(options?.title) ?? DEFAULT_IFRAME_TITLE,
                        sandbox: toNonEmptyString(options?.sandbox) ?? DEFAULT_IFRAME_SANDBOX,
                        wileyHosted: toNonEmptyString(options?.wileyHosted) ?? DEFAULT_IFRAME_WILEY_HOSTED,
                        allowFullscreen: options?.allowFullscreen,
                    });

                    if (dispatch) {
                        tr.replaceRangeWith(selection.from, selection.to, node);
                    }

                    return true;
                },
        };
    },
});
export const Iframe = IframeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(IframeNodeView);
    },
});
